import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';
import { Json } from '@/database.types';

// Interface for config field
interface AutopostConfig {
  image_generation_enabled?: boolean;
  video_generation_enabled?: boolean;
  [key: string]: any; // Allow for future config options
}

// Interface for BrandAutopostSettings
interface BrandAutopostSettings {
  id: string;
  brand_id: string;
  autogenerate_enabled: boolean;
  autopost_enabled: boolean;
  create_campaigns_automatically: boolean;
  autopost_campaign_prefix: string | null;
  config: AutopostConfig | null;
  metadata: Json | null;
  created_at: string;
  updated_at: string;
}

export const useBrandAutopostSettings = (brandId: string | undefined) => {
  const [brandAutopostSettings, setBrandAutopostSettings] = useState<BrandAutopostSettings | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch brand autopost settings
  useEffect(() => {
    if (!brandId) {
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('brand_autopost_settings')
          .select('*')
          .eq('brand_id', brandId)
          .single();

        if (error) {
          // Only throw if it's not a "no rows returned" error
          if (error.code !== 'PGRST116') {
            throw error;
          }
          // If no settings exist, set to null but don't show an error
          setBrandAutopostSettings(null);
          return;
        }

        // Cast the config field to the correct type
        setBrandAutopostSettings({
          ...data,
          config: data.config as AutopostConfig | null
        });
      } catch (error) {
        console.error('Error fetching brand autopost settings:', error);
        toast({
          title: 'Error',
          description: 'Failed to load brand autopost settings',
          variant: 'destructive',
        });
        // Set to null on error to allow creation
        setBrandAutopostSettings(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [brandId]);

  // Update brand autopost settings
  const updateBrandAutopostSettings = async (settings: Partial<BrandAutopostSettings>) => {
    if (!brandId) return;

    try {
      if (brandAutopostSettings) {
        // If updating config, merge with existing config
        let updateData: any = {
          ...settings,
          updated_at: new Date().toISOString()
        };

        if (settings.config !== undefined) {
          updateData.config = {
            ...(brandAutopostSettings.config || {}),
            ...settings.config
          };
        }

        // Update existing settings
        const { data, error } = await supabase
          .from('brand_autopost_settings')
          .update(updateData)
          .eq('id', brandAutopostSettings.id)
          .select()
          .single();

        if (error) throw error;

        // Cast the config field to the correct type
        setBrandAutopostSettings({
          ...data,
          config: data.config as AutopostConfig | null
        });
        toast({
          title: 'Settings Updated',
          description: 'Brand autopost settings have been updated successfully',
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });
      } else {
        // Create new settings
        const session = await supabase.auth.getSession();
        const userId = session.data.session?.user.id;

        if (!userId) {
          throw new Error('User ID not available. Please log in again.');
        }

        // Set default values for new settings
        const newSettings = {
          brand_id: brandId,
          autopost_enabled: settings.autopost_enabled !== undefined ? settings.autopost_enabled : false,
          create_campaigns_automatically: settings.create_campaigns_automatically !== undefined ? settings.create_campaigns_automatically : true,
          autopost_campaign_prefix: settings.autopost_campaign_prefix || 'Auto: ',
          config: settings.config || {
            image_generation_enabled: true,
            video_generation_enabled: true
          },
          metadata: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        const { data, error } = await supabase
          .from('brand_autopost_settings')
          .insert(newSettings)
          .select()
          .single();

        if (error) throw error;

        // Cast the config field to the correct type
        setBrandAutopostSettings({
          ...data,
          config: data.config as AutopostConfig | null
        });
        toast({
          title: 'Settings Created',
          description: 'Brand autopost settings have been created successfully',
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });
      }
    } catch (error) {
      console.error('Error updating brand autopost settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to update brand autopost settings',
        variant: 'destructive',
      });
    }
  };

  // Helper function to update just the config
  const updateConfig = async (configUpdates: Partial<AutopostConfig>) => {
    return updateBrandAutopostSettings({
      config: configUpdates as AutopostConfig
    });
  };

  return {
    brandAutopostSettings,
    loading,
    updateBrandAutopostSettings,
    updateConfig
  };
};

export default useBrandAutopostSettings;